import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetItem,
  ActionsheetScrollView,
} from '@/components/ui/actionsheet';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import type { ShareableSurvey } from '@/services/conversations/shareableSurveys';

type AttachSurveySheetProps = {
  isOpen: boolean;
  onClose: () => void;
  memberName: string;
  surveys: ShareableSurvey[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onSelect: (survey: ShareableSurvey) => void;
};

/**
 * Lists the surveys staff may share with the member, by name, for staff to
 * add one to their next message. Only surveys the viewer's rules let them
 * share arrive here (published engagement surveys), so every row is one the
 * send will accept. Everything
 * arrives as props: the sheet renders in the root overlay, outside the thread
 * screen's providers, so it cannot read the screen's context or queries itself.
 */
export function AttachSurveySheet({
  isOpen,
  onClose,
  memberName,
  surveys,
  isLoading,
  isError,
  onRetry,
  onSelect,
}: AttachSurveySheetProps) {
  const { bottom } = useSafeAreaInsets();

  return (
    <Actionsheet isOpen={isOpen} onClose={onClose}>
      <ActionsheetBackdrop />
      <ActionsheetContent className="max-h-[75%] w-full p-0" style={{ paddingBottom: bottom }}>
        <ActionsheetDragIndicatorWrapper>
          <ActionsheetDragIndicator />
        </ActionsheetDragIndicatorWrapper>

        <VStack className="w-full gap-1 px-5 pb-3 pt-2">
          <Heading size="md" className="text-typography-900">
            Share a survey
          </Heading>
          <Text className="text-sm text-typography-700">
            {`${memberName} gets a card to open and take it`}
          </Text>
        </VStack>

        <ActionsheetScrollView className="w-full" contentContainerClassName="px-2 pb-4">
          <AttachSurveyList
            surveys={surveys}
            isLoading={isLoading}
            isError={isError}
            onRetry={onRetry}
            onSelect={onSelect}
          />
        </ActionsheetScrollView>
      </ActionsheetContent>
    </Actionsheet>
  );
}

function AttachSurveyList({
  surveys,
  isLoading,
  isError,
  onRetry,
  onSelect,
}: Pick<AttachSurveySheetProps, 'surveys' | 'isLoading' | 'isError' | 'onRetry' | 'onSelect'>) {
  if (isLoading) {
    return (
      <HStack className="items-center justify-center py-10">
        <Spinner accessibilityLabel="Loading surveys" />
      </HStack>
    );
  }

  if (isError) {
    return (
      <VStack className="items-center gap-3 px-6 py-8">
        <Text className="text-center text-sm text-typography-700" accessibilityLiveRegion="polite">
          Could not load surveys.
        </Text>
        <Button variant="outline" size="sm" onPress={onRetry}>
          <ButtonText>Try again</ButtonText>
        </Button>
      </VStack>
    );
  }

  if (!surveys || surveys.length === 0) {
    return (
      <Text
        className="px-6 py-8 text-center text-sm text-typography-700"
        accessibilityLiveRegion="polite">
        No published surveys to share.
      </Text>
    );
  }

  return (
    <VStack>
      {surveys.map((survey) => (
        <ActionsheetItem
          key={survey.surveyGroupId}
          onPress={() => onSelect(survey)}
          accessibilityRole="button"
          accessibilityLabel={survey.name}
          className="min-h-[56px] rounded-xl px-3 py-2">
          <Text className="min-w-0 flex-1 text-base text-typography-900" numberOfLines={2}>
            {survey.name}
          </Text>
        </ActionsheetItem>
      ))}
    </VStack>
  );
}
